import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AssetManagement } from '@aptsi-types';
import { SystemAssetSourceService } from '../services/system-asset-source.service';

export interface SystemAssetsViewDialogData {
  assetSourceId: string;
  assetSourceName?: string;
}

interface SystemAssetPaginationMeta extends AssetManagement {
  totalSystemAssets?: number;
  totalPages?: number;
  currentPage?: number;
}

interface AssetSearchHeader {
  name: string;
  property: string;
  showInDropdown: boolean;
}

@Component({
  selector: 'app-system-assets-view',
  templateUrl: './system-assets-view.component.html',
  styleUrls: ['./system-assets-view.component.scss'],
})
export class SystemAssetsViewComponent implements OnInit {
  readonly itemsPerPage = 10;

  /** The UI uses one-based page numbers; API calls use zero-based page indexes. */
  currentPage = 1;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;
  totalItemsNormal = 0;
  totalItemsSearch = 0;

  isLoading = true;
  isSearching = false;
  loadError = false;
  searchError = false;
  issearch = false;

  searchText = '';
  filterByHeader = 'all';

  filteredData: AssetManagement[] = [];
  searchedData: AssetManagement[] = [];

  /** Prevent late responses from an earlier page/query from replacing current results. */
  private requestSequence = 0;

  readonly headers: AssetSearchHeader[] = [
    { name: 'Asset Name', property: 'systemAssetName', showInDropdown: true },
    { name: 'Description', property: 'systemAssetDescription', showInDropdown: true },
    { name: 'Owner', property: 'systemAssetOwner', showInDropdown: true },
    { name: 'Status', property: 'systemAssetStatus', showInDropdown: true },
    { name: 'Lifecycle Status', property: 'systemAssetLifecycleStatus', showInDropdown: true },
    { name: 'Endpoint', property: 'systemAssetEndpoint', showInDropdown: true },
    { name: 'Data Classification', property: 'dataClassification', showInDropdown: true },
    { name: 'Business Value', property: 'systemAssetBusinessValue', showInDropdown: true },
    { name: 'Unique Identifier', property: 'uniqueIdentifier', showInDropdown: true },
    { name: 'Imported By', property: 'importedBy', showInDropdown: true },
    { name: 'Imported At', property: 'importedAt', showInDropdown: true },
  ];

  get displayedData(): AssetManagement[] {
    return this.issearch ? this.searchedData : this.filteredData;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: SystemAssetsViewDialogData,
    private readonly dialogRef: MatDialogRef<SystemAssetsViewComponent>,
    private readonly systemAssetSourceService: SystemAssetSourceService,
  ) {}

  ngOnInit(): void {
    if (!this.data?.assetSourceId?.trim()) {
      this.loadError = true;
      this.isLoading = false;
      return;
    }

    void this.fetchSystemAssets();
  }

  /** Fetch one server-paginated page of assets for this source. */
  async fetchSystemAssets(): Promise<void> {
    const requestId = ++this.requestSequence;
    const requestedPage = Math.max(1, Math.floor(Number(this.currentPage) || 1));

    this.currentPage = requestedPage;
    this.isSearching = false;
    this.isLoading = true;
    this.loadError = false;

    try {
      const apiResponse = await this.systemAssetSourceService
        .FetchSystemAssetsByAssetSourceID(
          this.data.assetSourceId,
          this.toApiPageIndex(requestedPage),
        )
        .toPromise();

      // Ignore results from requests that were superseded by a page or search change.
      if (requestId !== this.requestSequence || this.issearch) {
        return;
      }

      const response: SystemAssetPaginationMeta[] = Array.isArray(apiResponse)
        ? (apiResponse as SystemAssetPaginationMeta[])
        : [];
      const metadata = response[0];
      this.filteredData = response;

      // Use pagination metadata supplied by the backend; do not calculate pages
      // from the response length or a client-side page-size assumption.
      const reportedTotalItems = this.readNonNegativeInteger(metadata?.totalSystemAssets);
      this.totalItemsNormal = reportedTotalItems ?? response.length;

      const reportedTotalPages = this.readNonNegativeInteger(metadata?.totalPages);
      this.totalPagesNormal = reportedTotalPages ?? 0;
      this.totalPages = this.totalPagesNormal;

      if (this.totalPagesNormal === 0) {
        this.currentPage = 1;
      } else if (requestedPage > this.totalPagesNormal) {
        // The result set may have shrunk while the dialog was open. Reload the last valid page.
        this.currentPage = this.totalPagesNormal;
        await this.fetchSystemAssets();
        return;
      } else {
        // Do not overwrite UI state from response.currentPage: some APIs return a differently
        // indexed or stale currentPage value. The requested UI page is the source of truth.
        this.currentPage = requestedPage;
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error fetching system assets for asset source:', error);
      this.loadError = true;
      this.filteredData = [];
      this.totalItemsNormal = 0;
      this.totalPagesNormal = 0;
      this.totalPages = 0;
      this.currentPage = 1;
    } finally {
      if (requestId === this.requestSequence) {
        this.isLoading = false;
      }
    }
  }

  /** Search is server-side and returns a paginated page; do not filter that page again locally. */
  async fetchSystemAssetsSearch(): Promise<void> {
    const requestId = ++this.requestSequence;
    const requestedPage = Math.max(1, Math.floor(Number(this.currentPage) || 1));

    this.currentPage = requestedPage;
    this.isLoading = false;
    this.isSearching = true;
    this.searchError = false;

    try {
      const apiResponse = await this.systemAssetSourceService
        .FetchSystemAssetsByAssetSourceIDSearch(
          this.data.assetSourceId,
          this.toApiPageIndex(requestedPage),
          this.searchText.trim(),
          this.filterByHeader,
        )
        .toPromise();

      if (requestId !== this.requestSequence || !this.issearch) {
        return;
      }

      const response: SystemAssetPaginationMeta[] = Array.isArray(apiResponse)
        ? (apiResponse as SystemAssetPaginationMeta[])
        : [];
      const metadata = response[0];
      this.searchedData = response;

      // Search pagination metadata also comes from the backend.
      const reportedTotalItems = this.readNonNegativeInteger(metadata?.totalSystemAssets);
      this.totalItemsSearch = reportedTotalItems ?? response.length;

      const reportedTotalPages = this.readNonNegativeInteger(metadata?.totalPages);
      this.totalPagesSearch = reportedTotalPages ?? 0;
      this.totalPages = this.totalPagesSearch;

      if (this.totalPagesSearch === 0) {
        this.currentPage = 1;
      } else if (requestedPage > this.totalPagesSearch) {
        this.currentPage = this.totalPagesSearch;
        await this.fetchSystemAssetsSearch();
        return;
      } else {
        this.currentPage = requestedPage;
      }
    } catch (error) {
      if (requestId !== this.requestSequence) {
        return;
      }

      console.error('Error searching system assets for asset source:', error);
      this.searchError = true;
      this.searchedData = [];
      this.totalItemsSearch = 0;
      this.totalPagesSearch = 0;
      this.totalPages = 0;
      this.currentPage = 1;
    } finally {
      if (requestId === this.requestSequence) {
        this.isSearching = false;
      }
    }
  }

  async searchSystemAssets(): Promise<void> {
    const query = this.searchText.trim();

    if (!query) {
      await this.clearSearch();
      return;
    }

    this.searchText = query;
    this.issearch = true;
    this.currentPage = 1;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.totalPages = 0;

    await this.fetchSystemAssetsSearch();
  }

  async clearSearch(): Promise<void> {
    // Invalidate an in-flight search immediately before returning to the normal list.
    ++this.requestSequence;
    this.searchText = '';
    this.issearch = false;
    this.searchError = false;
    this.currentPage = 1;
    this.searchedData = [];
    this.totalItemsSearch = 0;
    this.totalPagesSearch = 0;
    this.totalPages = this.totalPagesNormal;

    await this.fetchSystemAssets();
  }

  onInputChange(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
  }

  onFilterChange(): void {
    // Match the system-asset-capture screen: changing the filter returns to the normal list.
    void this.clearSearch();
  }

  changePage(page: number): void {
    const activeTotalPages = this.issearch ? this.totalPagesSearch : this.totalPagesNormal;

    if (
      !Number.isInteger(page) ||
      page < 1 ||
      page > activeTotalPages ||
      page === this.currentPage
    ) {
      return;
    }

    this.currentPage = page;
    if (this.issearch) {
      void this.fetchSystemAssetsSearch();
    } else {
      void this.fetchSystemAssets();
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  trackBySystemAssetId(index: number, asset: AssetManagement): string | number {
    return asset.systemAssetId || asset.uniqueIdentifier || index;
  }

  /** Convert a one-based UI page to the zero-based page index used by the service. */
  private toApiPageIndex(uiPage: number): number {
    return Math.max(0, Math.floor(uiPage) - 1);
  }

  private readNonNegativeInteger(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
  }

}
